import { useEffect } from 'react'
import { Plus, Trash2, GripVertical } from 'lucide-react'
import type { ExerciseEditorProps } from '@/types'
import { encodeOrderingCorrectAnswer } from '@/lib/exerciseUtils'

interface Props extends ExerciseEditorProps {
  orderItems: string[]
  setOrderItems: React.Dispatch<React.SetStateAction<string[]>>
}

export default function OrderingEditor({ formData, setFormData, isViewMode, orderItems, setOrderItems }: Props) {
  // Sincronizar items -> formData al cambiar
  useEffect(() => {
    const filtered = orderItems.filter(item => item.trim() !== '')
    setFormData(prev => ({
      ...prev,
      options: orderItems,
      correct_answer: encodeOrderingCorrectAnswer(filtered)
    }))
  }, [orderItems])

  const addItem = () => {
    if (orderItems.length >= 10) return
    setOrderItems(prev => [...prev, ''])
  }

  const removeItem = (index: number) => {
    if (orderItems.length <= 2) return
    setOrderItems(prev => prev.filter((_, i) => i !== index))
  }

  const updateItem = (index: number, value: string) => {
    setOrderItems(prev => prev.map((item, i) => i === index ? value : item))
  }

  return (
    <div className="space-y-4">
      {/* Instrucciones */}
      <div className="p-3 bg-cyan-50 border border-cyan-200 rounded-lg">
        <p className="text-sm text-cyan-800">
          Escribe los elementos en el <strong>orden correcto</strong>. El estudiante los verá
          desordenados y deberá organizarlos.
        </p>
      </div>

      {/* Pregunta/Instruccion */}
      <div>
        <label htmlFor="ordering-instruction" className="block text-sm font-medium text-gray-700 mb-1">
          Instruccion del ejercicio
        </label>
        <input id="ordering-instruction" type="text" value={formData.question}
          onChange={(e) => setFormData(prev => ({ ...prev, question: e.target.value }))}
          placeholder="Ordena las palabras para formar la oracion correcta..."
          className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-[#0055A4] focus:border-[#0055A4] disabled:bg-gray-100 disabled:cursor-not-allowed"
          required
          disabled={isViewMode}
        />
      </div>

      {/* Items en orden correcto */}
      <div>
        <span className="text-sm font-medium text-gray-700 mb-2 block">
          Elementos (en orden correcto)
        </span>
        <div className="space-y-2">
          {orderItems.map((item, index) => (
            <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 border rounded-lg">
              <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
              <span className="text-sm font-bold text-cyan-600 w-6 text-center flex-shrink-0">
                {index + 1}.
              </span>
              <input
                type="text"
                value={item}
                onChange={(e) => updateItem(index, e.target.value)}
                placeholder={`Elemento ${index + 1}...`}
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                disabled={isViewMode}
              />
              {orderItems.length > 2 && !isViewMode && (
                <button
                  type="button"
                  onClick={() => removeItem(index)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Agregar elemento */}
      {!isViewMode && orderItems.length < 10 && (
        <button
          type="button"
          onClick={addItem}
          className="flex items-center gap-2 text-sm text-cyan-600 hover:text-cyan-700 font-medium"
        >
          <Plus className="w-4 h-4" />
          Agregar elemento ({orderItems.length}/10)
        </button>
      )}
    </div>
  )
}
